import React from 'react';

import { Badge, Box, MenuItem, Popover, Stack, Typography } from '@mui/material';

import { NetworkRequestOutcome } from '@/parsing/parsers/common/NetworkRequestOutcome';

import {
  ALL_NETWORK_OUTCOMES,
  isNetworkOutcomeFilterActive,
  makeEmptyNetworkOutcomeFilter,
  NetworkOutcomeFilter,
  NetworkOutcomeFilterSide,
  OUTCOME_STYLES,
  setSide,
  toggleOutcome,
} from './NetworkOutcomeFilter';

interface Props {
  filter: NetworkOutcomeFilter;
  onChange: (filter: NetworkOutcomeFilter) => void;
}

interface State {
  isOpen: boolean;
}

function OutcomePill({
  outcome,
  selected,
  onClick,
}: {
  outcome: NetworkRequestOutcome;
  selected: boolean;
  onClick: () => void;
}) {
  const s = OUTCOME_STYLES[outcome];
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '4px',
        px: 1,
        py: 0.4,
        fontSize: '0.8rem',
        fontWeight: selected ? 700 : 400,
        userSelect: 'none',
        transition: 'opacity 0.12s',
        backgroundColor: selected ? s.bgColor : 'transparent',
        color: selected ? s.textColor : 'rgba(0,0,0,0.4)',
        border: `2px solid ${selected ? s.borderColor : 'rgba(0,0,0,0.12)'}`,
        '&:hover': {
          backgroundColor: s.bgColor,
          color: s.textColor,
          borderColor: s.borderColor,
        },
      }}
    >
      {s.label}
    </Box>
  );
}

function SidePill({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '4px',
        px: 1.5,
        py: 0.5,
        fontSize: '0.85rem',
        fontWeight: selected ? 700 : 400,
        userSelect: 'none',
        transition: 'opacity 0.12s',
        backgroundColor: selected ? 'rgba(25,118,210,0.12)' : 'transparent',
        color: selected ? 'rgb(25,118,210)' : 'rgba(0,0,0,0.4)',
        border: `2px solid ${selected ? 'rgb(25,118,210)' : 'rgba(0,0,0,0.12)'}`,
        '&:hover': {
          backgroundColor: 'rgba(25,118,210,0.12)',
          color: 'rgb(25,118,210)',
          borderColor: 'rgb(25,118,210)',
        },
      }}
    >
      {label}
    </Box>
  );
}

export class NetworkOutcomeFilterMenuItem extends React.Component<Props, State> {
  private anchorElement = React.createRef<HTMLLIElement>();

  constructor(props: Props) {
    super(props);
    this.state = { isOpen: false };
  }

  render() {
    const { filter } = this.props;
    const isActive = isNetworkOutcomeFilterActive(filter);
    const badgeCount = isActive ? filter.outcomes.length : null;

    return [
      <MenuItem
        key="trigger"
        ref={this.anchorElement}
        onClick={() => this.setState({ isOpen: true })}
      >
        <Badge
          badgeContent={badgeCount}
          color="secondary"
          showZero={false}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Typography textAlign="center">Filter by network state</Typography>
        </Badge>
      </MenuItem>,

      <Popover
        key="popover"
        open={this.state.isOpen}
        anchorEl={this.anchorElement.current}
        onClose={() => this.setState({ isOpen: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: '10px', boxShadow: '0 4px 24px rgba(0,0,0,0.13)' } }}
      >
        <Box sx={{ p: 2, minWidth: 340 }}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Filter by network state
            </Typography>
            {isActive && (
              <Typography
                variant="caption"
                onClick={() => this.props.onChange(makeEmptyNetworkOutcomeFilter())}
                sx={{
                  cursor: 'pointer',
                  color: 'rgb(25,118,210)',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Clear all
              </Typography>
            )}
          </Stack>

          {/* Side selector */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            {(
              [
                [NetworkOutcomeFilterSide.Both, 'Both'],
                [NetworkOutcomeFilterSide.Requests, 'Requests'],
                [NetworkOutcomeFilterSide.Responses, 'Responses'],
              ] as [NetworkOutcomeFilterSide, string][]
            ).map(([side, label]) => (
              <SidePill
                key={side}
                label={label}
                selected={filter.side === side}
                onClick={() => this.props.onChange(setSide(filter, side))}
              />
            ))}
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' }}>
              is
            </Typography>
          </Stack>

          {/* Outcome pills */}
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {ALL_NETWORK_OUTCOMES.map((outcome) => (
              <OutcomePill
                key={outcome}
                outcome={outcome}
                selected={filter.outcomes.includes(outcome)}
                onClick={() => this.props.onChange(toggleOutcome(filter, outcome))}
              />
            ))}
          </Stack>

          {/* Footer */}
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1.5, color: 'rgba(0,0,0,0.4)' }}
          >
            {isActive
              ? `Showing ${
                  filter.side === NetworkOutcomeFilterSide.Both
                    ? 'requests & responses'
                    : filter.side
                } where state is ${filter.outcomes
                  .map((o) => OUTCOME_STYLES[o].label)
                  .join(' or ')}`
              : 'Select a state above to filter'}
          </Typography>
        </Box>
      </Popover>,
    ];
  }
}
